import React from 'react'
import { QuoteCard } from './QuoteCard'
import { VehicleCardBody } from './VehicleCardBody'
import { wonOppToQuote } from './wonOppToQuote'
import { vehicleToQuote } from './vehicleToQuote'

// One owned car as a quote card. The adapter translates the sale into
// QuoteCard's props here, so the card itself never learns about opportunities,
// pinned deals or maturity dates. Neither card shows a status dot: once a car
// is delivered, a quote's Active/Expired program window means nothing.
const CurrentVehicleCard = ({ opportunity, onOpen, onExtendLease, onPurchaseVehicle, onRemoveVehicle, onReverseSale }) => {
    const quote = wonOppToQuote(opportunity)
    // The adapted type, never opportunity.deal_type: that column defaults to
    // LEASE in the database, so an imported cash sale would otherwise be offered
    // a lease extension. wonOppToQuote already resolves the pinned Deal first.
    const isLease = (quote.deal_type || '').toUpperCase() === 'LEASE'

    return (
        <QuoteCard
            quote={quote}
            vehicleBody={<VehicleCardBody quote={quote} />}
            showStatus={false}
            // Always owned, so QuoteCard never opens the worksheet itself.
            onCardClick={() => onOpen?.(opportunity)}
            // No delete item: neither removal deletes the pinned Deal. "Remove
            // Vehicle" keeps the sale; "Delete Vehicle and history" reopens it
            // as a lead.
            actions={[
                ...(isLease && onExtendLease
                    ? [{ label: 'Extend Lease', onClick: () => onExtendLease(opportunity) }]
                    : []),
                ...(onPurchaseVehicle
                    ? [{ label: 'Purchase Vehicle', onClick: () => onPurchaseVehicle(opportunity) }]
                    : []),
                ...(onRemoveVehicle
                    ? [{ label: 'Remove Vehicle', danger: true, onClick: () => onRemoveVehicle(opportunity) }]
                    : []),
                ...(onReverseSale
                    ? [{ label: 'Delete Vehicle and history', danger: true, onClick: () => onReverseSale(opportunity) }]
                    : []),
            ]}
        />
    )
}

// One hand-recorded car (CrmVehicle). It owns no worksheet, so clicking reopens
// the car itself for editing rather than a quote it doesn't have; the "⋯" menu
// still deletes the record.
//
// It says "Remove Vehicle" like the sold cards beside it — the rep is answering
// the same question either way — but a hand-recorded car has no sale behind it
// to preserve, so the one menu item is still the existing hard delete.
const OwnedVehicleCard = ({ vehicle, onEditVehicle, onDeleteVehicle, onExtendLease, onPurchaseVehicle }) => {
    const quote = vehicleToQuote(vehicle)

    return (
        <QuoteCard
            quote={quote}
            vehicleBody={<VehicleCardBody quote={quote} />}
            showStatus={false}
            deleteLabel="Remove Vehicle"
            onCardClick={onEditVehicle ? () => onEditVehicle(vehicle) : undefined}
            onDelete={onDeleteVehicle ? () => onDeleteVehicle(vehicle.id) : undefined}
            actions={[
                // Only what the rep actually recorded. A blank ownership_type means
                // they never said, and a car that was never leased has no lease to extend.
                ...(vehicle?.ownership_type === 'LEASE' && onExtendLease
                    ? [{ label: 'Extend Lease', onClick: () => onExtendLease(vehicle) }]
                    : []),
                // Any car can be bought, whatever its ownership.
                ...(onPurchaseVehicle
                    ? [{ label: 'Purchase Vehicle', onClick: () => onPurchaseVehicle(vehicle) }]
                    : []),
            ]}
        />
    )
}

// The cars this person owns, from both sources: vehicles recorded by hand, then
// the WON opportunities that recorded a sale. Neither replaces the other —
// adding a car closes nothing, and closing a deal creates no vehicle record.
export const CurrentVehiclesSection = ({
    opportunities, vehicles = [], onAddCar, onEditVehicle, onDeleteVehicle,
    onOpenOpportunityVehicle, onExtendVehicleLease, onExtendOpportunityLease, onRemoveOpportunityVehicle,
    onReverseSale, onPurchaseVehicle, onPurchaseOpportunityVehicle,
}) => {
    const count = opportunities.length + vehicles.length

    return (
        <div className="panel">
            <div className="quotes-header">
                <div className="panel-title">Current Vehicles</div>
                <button className="cbtn-primary-sm" style={{ marginLeft: 'auto' }} onClick={onAddCar}>+ Add Car</button>
            </div>
            <div className="quotes-body">
                {count
                    ? <div className="quotes-grid">
                        {/* Both lists arrive newest-first from the API; hand-entered
                            cars lead so a just-added one is where the eye lands. */}
                        {vehicles.map((v) => (
                            <OwnedVehicleCard
                                key={`v${v.id}`}
                                vehicle={v}
                                onEditVehicle={onEditVehicle}
                                onDeleteVehicle={onDeleteVehicle}
                                onExtendLease={onExtendVehicleLease}
                                onPurchaseVehicle={onPurchaseVehicle}
                            />
                        ))}
                        {opportunities.map((o) => (
                            <CurrentVehicleCard
                                key={o.id}
                                opportunity={o}
                                onOpen={onOpenOpportunityVehicle}
                                onExtendLease={onExtendOpportunityLease}
                                onPurchaseVehicle={onPurchaseOpportunityVehicle}
                                onRemoveVehicle={onRemoveOpportunityVehicle}
                                onReverseSale={onReverseSale}
                            />
                        ))}
                    </div>
                    : <div className="empty-mini">No cars yet. Add a vehicle or mark an opportunity as sold to record one.</div>}
            </div>
        </div>
    )
}
